import { FileIcon, styled } from 'opensolar-ui'
import { FileRecord } from '../controller/UploadFileContext'

const ThumbnailImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
})

const ThumbnailIconContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[100],
  padding: 4,
  borderRadius: 0,
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
}))

const StyledFileIcon = styled(FileIcon)(({ theme }) => ({
  color: theme.palette.grey[600],
  maxHeight: 100,
  height: '90%',
  width: 'unset',
}))

type FileThumbnailProps = {
  record: FileRecord
  className?: string
}

/**
 * Helper to check if a file is an image based on its name/URL
 */
export const isImageFile = (record: FileRecord): boolean => {
  const name = record.name?.toLowerCase() || ''
  const url = record.url?.toLowerCase() || ''
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp']
  return imageExtensions.some((ext) => name.endsWith(ext) || url.includes(ext))
}

/**
 * Get the preview URL for a file record
 */
export const getPreviewUrl = (record: FileRecord): string | undefined => {
  if (record.file) {
    // Always prefer the file object URL if it exists, it will always be faster
    return URL.createObjectURL(record.file)
  }
  if (record.fileRecord?.file_contents) {
    return record.fileRecord.file_contents
  }
  return record.url
}

/**
 * Unified component for displaying file thumbnails (image or icon)
 * Used by both UploadFileGrid and UploadFileList
 */
export const UploadFileThumbnail = ({ record, className }: FileThumbnailProps) => {
  const previewUrl = getPreviewUrl(record)
  const isImage = isImageFile(record)

  if (isImage && previewUrl) {
    return <ThumbnailImage src={previewUrl} alt={record.name} className={className} />
  }

  return (
    <ThumbnailIconContainer className={className}>
      <StyledFileIcon />
    </ThumbnailIconContainer>
  )
}
